import { useEffect, useRef, useState } from 'react'
import IssuesService from '../../../services/api/IssuesService'
import IssueStatusesService from '../../../services/api/IssueStatusesService'

const FULL_NAME_KEY = 'full_name'
const noop = () => null

const toUserOptions = response => (response || []).map(option => ({
  id: option.user_id,
  [FULL_NAME_KEY]: option.user_name
}))

/**
 * Lazily fetches, per collect_id, the issue statuses and assignable users
 * needed by the inline editors. Each collect is fetched (or in flight) at
 * most once and cached for the lifetime of the page.
 */
const useEditorLazyData = (editorMode, editingIssue) => {
  const [statusesByCollect, setStatusesByCollect] = useState({})
  const [usersByCollect, setUsersByCollect] = useState({})
  const requestedStatusesRef = useRef(new Set())
  const requestedUsersRef = useRef(new Set())
  const targetCollectId = editingIssue?.collect_id ?? null

  useEffect(() => {
    if (editorMode !== 'status' || !targetCollectId || requestedStatusesRef.current.has(targetCollectId))
      return noop
    requestedStatusesRef.current.add(targetCollectId)
    let cancelled = false
    IssueStatusesService.fetchAll(targetCollectId).then(response => {
      if (cancelled)
        return
      setStatusesByCollect(previous => ({
        ...previous, [targetCollectId]: response.issue_statuses || []
      }))
    }).catch(() => {
      // Drop the guard so a transient failure can be retried on the next open.
      requestedStatusesRef.current.delete(targetCollectId)
    })
    return () => {
      cancelled = true
    }
  }, [editorMode, targetCollectId])

  useEffect(() => {
    if (editorMode !== 'attribution' || !targetCollectId || requestedUsersRef.current.has(targetCollectId))
      return noop
    requestedUsersRef.current.add(targetCollectId)
    let cancelled = false
    IssuesService.getAssignableUsers(targetCollectId).then(response => {
      if (cancelled)
        return
      setUsersByCollect(previous => ({ ...previous, [targetCollectId]: toUserOptions(response) }))
    }).catch(() => {
      // Drop the guard so a transient failure can be retried on the next open.
      requestedUsersRef.current.delete(targetCollectId)
    })
    return () => {
      cancelled = true
    }
  }, [editorMode, targetCollectId])

  const currentIssueStatuses = targetCollectId ? statusesByCollect[targetCollectId] || [] : []
  const currentAvailableUsers = targetCollectId ? usersByCollect[targetCollectId] || [] : []

  return { currentIssueStatuses, currentAvailableUsers }
}

export default useEditorLazyData
